import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { signOutUser, useAuth, api } from '@weebee/api';
import { Building2, RefreshCw } from 'lucide-react';

export const Route = createFileRoute('/no-company')({
  component: NoCompanyPage,
});

function NoCompanyPage() {
  const authState = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect to login when signed out
  useEffect(() => {
    if (authState.status === 'unauthenticated') {
      void navigate({ to: '/login' });
    }
  }, [authState.status, navigate]);

  const handleRetry = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      const { data, error: apiError } = await api.GET('/api/v1/corporate/memberships');

      if (apiError) {
        const detail = (apiError as { detail?: string }).detail ?? 'Unknown error';
        const status = (apiError as { status?: number }).status;
        console.error('[no-company] Memberships API error:', { status, detail, apiError });
        setError(`Failed to check memberships: ${detail}`);
        return;
      }

      if (data?.memberships?.length) {
        void navigate({ to: '/app' });
        return;
      }

      console.info('[no-company] No memberships found for current user');
      setError('No company membership found yet. Verify your work email in the app first.');
    } catch (err) {
      console.error('[no-company] Network error checking memberships:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setChecking(false);
    }
  }, [navigate]);

  const handleSignOut = useCallback(async () => {
    await signOutUser();
    // useEffect above handles redirect to /login
  }, []);

  if (authState.status !== 'authenticated') {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 px-4 text-center">
        <Building2 className="h-16 w-16 mx-auto text-muted-foreground/40" />
        <div>
          <h1 className="text-xl font-bold text-foreground">No company found</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Link your work email in the WeeBee app to access your company dashboard.
          </p>
        </div>

        <div className="rounded-lg border border-border bg-card p-4 text-left">
          <p className="text-sm font-medium text-foreground mb-2">How to get started:</p>
          <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
            <li>Open the WeeBee mobile app</li>
            <li>Go to Settings &rarr; Work Email</li>
            <li>Verify your company email address</li>
            <li>Come back here and tap &ldquo;Check again&rdquo;</li>
          </ol>
        </div>

        {error && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive text-left">
            {error}
          </div>
        )}

        <button
          type="button"
          disabled={checking}
          onClick={() => void handleRetry()}
          className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} />
          {checking ? 'Checking...' : 'Check again'}
        </button>

        <button
          type="button"
          onClick={() => void handleSignOut()}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
